import { COLOR_BY_ID, DEFAULT_COLOR, luminance } from '@/lib/losi-engine/skins'

/** Live theme pulled from the customiser body + page paper. */
export interface GameTheme {
  /** Playfield backdrop — follows page `--paper` / `--background`. */
  paper: string
  /** Character fill — paddles / ships / idle pads. Always contrasts with paper. */
  body: string
  /** Lit flash / ball / bullet — always pops on paper (and vs idle body). */
  mark: string
  /** Soft lines / muted chrome on paper. */
  mute: string
  /** True when paper is dark (overlay chrome). */
  paperDark: boolean
}

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') {
    return fallback
  }
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

function withAlpha(hex: string, a: number): string {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16)
  if (!Number.isFinite(n)) return `rgba(10,10,12,${a})`
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r},${g},${b},${a})`
}

function asHex(raw: string, fallback: string): string {
  const t = raw.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1]!
    const g = t[2]!
    const b = t[3]!
    return `#${r}${r}${g}${g}${b}${b}`
  }
  return fallback
}

function contrasts(a: string, b: string, min = 0.22): boolean {
  return Math.abs(luminance(a) - luminance(b)) >= min
}

/**
 * Resolve game colours from the Losi on screen — same light/dark idea as the
 * bot: body and mark must read on paper, or we force ink / cream.
 */
export function resolveGameTheme(colorId: string): GameTheme {
  const bodyRaw = COLOR_BY_ID.get(colorId)?.hex ?? COLOR_BY_ID.get(DEFAULT_COLOR)!.hex
  const paper = asHex(
    cssVar('--paper', '') || cssVar('--background', '#0a0a0c'),
    '#0a0a0c'
  )
  const paperDark = luminance(paper) < 0.35
  const onPaper = paperDark ? '#f1efe9' : '#0a0a0c'
  const body = contrasts(bodyRaw, paper) ? bodyRaw : onPaper
  let mark = contrasts(bodyRaw, paper) ? bodyRaw : onPaper
  if (!contrasts(mark, paper) || !contrasts(mark, body, 0.12)) {
    mark = onPaper
  }
  if (!contrasts(mark, body, 0.12)) {
    mark = paperDark ? '#ffffff' : '#111111'
  }
  return {
    paper,
    body,
    mark,
    mute: withAlpha(onPaper, paperDark ? 0.4 : 0.2),
    paperDark
  }
}
